import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { SyllabusQuery } from '@examdojo/category';
import { ToastService } from '@examdojo/core/toast';
import {
  DerivativeQuestionReviewType,
  QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION,
  REFERENCE_QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION,
  ReferenceQuestionReviewType,
  ReviewPermissionsQuery,
} from '@examdojo/review';
import { RootUrlParts, ValidateUrlParts } from '../app.model';
import { SYLLABUS_SLUG_PARAM } from './validation-routing.model';

function getSyllabusSlugFromRoute(route: ActivatedRouteSnapshot): string | null {
  let currentRoute: ActivatedRouteSnapshot | null = route;
  while (currentRoute) {
    const syllabusSlug = currentRoute.paramMap.get(SYLLABUS_SLUG_PARAM);
    if (syllabusSlug) {
      return syllabusSlug;
    }
    currentRoute = currentRoute.parent;
  }
  return null;
}

function getQuestionTypeFromRoute(route: ActivatedRouteSnapshot): 'derivative' | 'reference' | null {
  let currentRoute: ActivatedRouteSnapshot | null = route;
  while (currentRoute) {
    const pathSegments = currentRoute.url.map((segment) => segment.path);
    if (pathSegments.includes(ValidateUrlParts.DerivativeQuestion)) {
      return 'derivative';
    }
    if (pathSegments.includes(ValidateUrlParts.ReferenceQuestion)) {
      return 'reference';
    }
    currentRoute = currentRoute.parent;
  }
  return null;
}

function findFirstAvailableMode(
  reviewPermissionsQuery: ReviewPermissionsQuery,
  syllabusId: number,
  syllabusSlug: string,
): string | null {
  for (const mode of Object.values(DerivativeQuestionReviewType)) {
    const permission = QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[mode];
    if (reviewPermissionsQuery.hasReviewPermission(syllabusId, permission)) {
      return `/syllabus/${syllabusSlug}/validate/derivative-question/${mode}`;
    }
  }

  for (const mode of Object.values(ReferenceQuestionReviewType)) {
    const permission = REFERENCE_QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[mode];
    if (reviewPermissionsQuery.hasReviewPermission(syllabusId, permission)) {
      return `/syllabus/${syllabusSlug}/validate/reference-question/${mode}`;
    }
  }

  return null;
}

export const validateModePermissionsGuard: CanActivateFn = (route) => {
  const reviewPermissionsQuery = inject(ReviewPermissionsQuery);
  const syllabusQuery = inject(SyllabusQuery);
  const router = inject(Router);
  const toastService = inject(ToastService);

  const syllabusSlug = getSyllabusSlugFromRoute(route);
  const syllabus = syllabusSlug && syllabusQuery.getBySlug(syllabusSlug);
  const mode = route.paramMap.get('mode');
  const questionType = getQuestionTypeFromRoute(route);

  if (!syllabusSlug || !syllabus || !mode || !questionType) {
    return router.createUrlTree([`/${RootUrlParts.Overview}`]);
  }

  const permission =
    questionType === 'derivative'
      ? QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[mode as DerivativeQuestionReviewType]
      : REFERENCE_QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[mode as ReferenceQuestionReviewType];

  if (reviewPermissionsQuery.hasReviewPermission(syllabus.id, permission)) {
    return true;
  }

  const fallbackRoute = findFirstAvailableMode(reviewPermissionsQuery, syllabus.id, syllabusSlug);

  if (fallbackRoute) {
    return router.createUrlTree([fallbackRoute]);
  }

  toastService.error('You do not have permission to access any review modes for this syllabus.');
  return router.createUrlTree([`/${RootUrlParts.Overview}`]);
};
